import React, { Component } from 'react';

import { Button, Flexbox } from '@schoolinks/ui-components';
import { isEqual } from 'lodash';
import { bool, func, number, arrayOf, shape } from 'prop-types';
import { withTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { connect } from 'react-redux';
import { withState } from 'recompose';

import CircularProgress from '@mui/material/CircularProgress';

import TextLink from 'components/Common/TextLink';
import { NOTIFICATIONS } from 'routes/constants';

import Divider from 'modules/Notifications/components/Divider';
import NotificationItem from 'modules/Notifications/components/NotificationItem';

import s from './NotificationsContainer.css';

@connect(
  ({ notifications: { count, notifications } }) => ({
    count,
    notifications,
  }),
  null,
)
@withState('loading', 'setLoading', false)
@withTranslation()
export default class NotificationsContainer extends Component {
  async componentDidMount() {
    const { setLoading, notifications, getInitialNotifications } = this.props;
    if (notifications.length) {
      await getInitialNotifications();
    } else {
      setLoading(true);
      await getInitialNotifications();
      setLoading(false);
    }
  }

  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  render() {
    const {
      loading,
      notifications,
      count,
      unreadCount,
      getNextNotifications,
      markNotificationRead,
      markAllNotificationSeen,
      markAllNotificationRead,
      t,
    } = this.props;

    const hasNotifications = notifications?.length > 0;

    return (
      <div className={s.root}>
        <Flexbox
          alignItems="center"
          justifyContent="space-between"
          className={s.header}
        >
          <div className={s.headerTitle}>
            {t('Notifications.NotificationsContainer.Notifications')}
          </div>
          <Button
            color="blue"
            variant="text"
            onClick={async () => {
              if (unreadCount > 0) {
                await Promise.all([
                  markAllNotificationSeen(),
                  markAllNotificationRead(),
                ]);
              }
            }}
            label={t('Notifications.NotificationsContainer.MarkAllAs')}
          />
        </Flexbox>
        <Divider />
        {loading && !hasNotifications && (
          <div className={s.loader}>
            <CircularProgress
              aria-label={t(
                'Notifications.NotificationsContainer.LoadingNotifications',
              )}
            />
          </div>
        )}
        {!loading && hasNotifications ? (
          <div className={s.infiniteScrollContainer}>
            <InfiniteScroll
              pageStart={0}
              initialLoad={false}
              loadMore={getNextNotifications}
              hasMore={notifications.length < count}
              loader={
                <div className={s.loader}>
                  <CircularProgress
                    aria-label={t(
                      'Notifications.NotificationsContainer.LoadingNotifications',
                    )}
                  />
                </div>
              }
              threshold={60}
              useWindow={false}
            >
              {notifications.map(el => (
                <NotificationItem
                  key={el.id}
                  className={s.notificationItem}
                  notification={el}
                  markNotificationRead={() => markNotificationRead(el.id)}
                />
              ))}
            </InfiniteScroll>
          </div>
        ) : (
          <div className={s.emptyText}>
            {t('Notifications.NotificationsContainer.NoNewNotifications')}{' '}
            <span
              role="img"
              aria-label={t('Notifications.NotificationsContainer.ThumbsUp')}
            >
              👍
            </span>
          </div>
        )}
        <Divider />
        <Flexbox
          alignItems="center"
          justifyContent="center"
          className={s.footer}
        >
          <TextLink color="grayDark" variant="titleMd500" href={NOTIFICATIONS}>
            {t('Notifications.NotificationsContainer.SeeAllNotifications')}
          </TextLink>
        </Flexbox>
      </div>
    );
  }
}

NotificationsContainer.propTypes = {
  loading: bool,
  setLoading: func,
  count: number,
  unreadCount: number,
  notifications: arrayOf(shape({})),
  getInitialNotifications: func.isRequired,
  getNextNotifications: func.isRequired,
  markNotificationRead: func.isRequired,
  markAllNotificationSeen: func.isRequired,
  markAllNotificationRead: func.isRequired,
  t: func.isRequired,
};

NotificationsContainer.defaultProps = {
  notifications: [],
  count: undefined,
  loading: false,
  setLoading: undefined,
  unreadCount: undefined,
};
